/**
 * ScenarioLoader
 * 
 * Responsible for:
 * - Fetching scenario JSON from backend
 * - Running all validations (scene IDs, references, conditions, assets)
 * - Preloading all assets with progress tracking
 * - Refusing to start if validation fails
 * - Caching loaded scenarios
 */

import type { Scenario, Scene, CharacterPortrait } from '../types';
import { validateScenarioStructure } from '../types/validation';
import { isVideoAsset, preloadVideo } from './media-utils';
import {
  getScenarioAssetCacheKey,
  resolveScenarioAssetUrl,
  type ScenarioAssetLayer,
} from './scenario-asset-url';

export interface LoadProgress {
  stage: 'fetching' | 'validating' | 'preloading' | 'complete';
  message: string;
  percent: number;
  assetsLoaded?: number;
  assetsTotal?: number;
}

export interface LoadResult {
  success: boolean;
  scenario?: Scenario;
  error?: string;
  validationErrors?: string[];
}

export type ProgressCallback = (progress: LoadProgress) => void;

/**
 * Cache for loaded scenarios to avoid repeated fetching
 */
const scenarioCache = new Map<string, Scenario>();

/**
 * Cache for preloaded images
 */
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Collects all asset paths from a scenario
 */
function collectAssetPaths(scenario: Scenario): string[] {
  const assets = new Set<string>();
  
  console.log('=== collectAssetPaths START ===');
  console.log('scenario.metadata:', scenario.metadata);
  console.log('scenario.metadata.skills:', scenario.metadata.skills);

  // Add metadata thumbnail
  if (scenario.metadata.thumbnail) {
    console.log('Adding thumbnail:', scenario.metadata.thumbnail);
    assets.add(scenario.metadata.thumbnail);
  }

  // Add skill icons
  if (scenario.metadata.skills) {
    console.log('Skills found:', scenario.metadata.skills.length);
    for (const skill of scenario.metadata.skills) {
      console.log('Processing skill:', skill);
      if (skill.icon) {
        console.log('Adding skill icon:', skill.icon);
        assets.add(skill.icon);
      } else {
        console.warn('Skill has no icon:', skill);
      }
    }
  } else {
    console.warn('No skills in metadata!');
  }

  // Add character portraits and icons
  if (scenario.metadata.characters) {
    console.log('Characters found:', scenario.metadata.characters.length);
    for (const character of scenario.metadata.characters) {
      console.log('Processing character:', character);
      // Add icon
      if (character.icon) {
        console.log('Adding character icon:', character.icon);
        assets.add(character.icon);
      }
      // Add default image
      if (character.defaultImage) {
        console.log('Adding character default image:', character.defaultImage);
        assets.add(character.defaultImage);
      }
      // Add emotion images
      if (character.emotions) {
        Object.values(character.emotions).forEach(emotionPath => {
          if (emotionPath) {
            console.log('Adding character emotion image:', emotionPath);
            assets.add(emotionPath);
          }
        });
      }
    }
  }

  // Add item icons
  if (scenario.items) {
    for (const item of scenario.items) {
      if (item.icon) {
        assets.add(item.icon);
      }
    }
  }

  // Recursively collect from scenes
  function collectFromScenes(scenes: Scene[]): void {
    for (const scene of scenes) {
      // Add background
      if (scene.background) {
        assets.add(scene.background);
      }

      // Add element images
      if (scene.elements) {
        for (const element of scene.elements) {
          if (element.image) {
            assets.add(element.image);
          }
          // Add mood images if any
          if (element.moods) {
            Object.values(element.moods).forEach(moodPath => {
              if (moodPath) assets.add(moodPath);
            });
          }
        }
      }

      // Recursively process nested branch scenes (choice options)
      if (scene.type === 'choice') {
        for (const option of scene.options ?? []) {
          if (option.scenes?.length) {
            collectFromScenes(option.scenes);
          }
        }
      }
    }
  }

  collectFromScenes(scenario.scenes);

  const assetArray = Array.from(assets);
  console.log('=== collectAssetPaths END ===');
  console.log('Total assets collected:', assetArray.length);
  console.log('Assets:', assetArray);
  return assetArray;
}

/**
 * Validates that all asset paths exist and are loadable
 */
async function validateAssets(
  scenarioId: string,
  assetPaths: string[],
  onProgress?: ProgressCallback
): Promise<{ valid: boolean; errors: string[] }> {
  const errors: string[] = [];
  const baseUrl = `/scenarios/${scenarioId}`;

  for (let i = 0; i < assetPaths.length; i++) {
    const assetPath = assetPaths[i];
    if (!assetPath) continue; // Skip undefined paths
    
    const fullUrl = assetPath.startsWith('http') ? assetPath : `${baseUrl}/${assetPath}`;

    if (onProgress) {
      onProgress({
        stage: 'validating',
        message: `Validating assets (${i + 1}/${assetPaths.length})`,
        percent: 10 + (i / assetPaths.length) * 20, // 10-30%
      });
    }

    try {
      // Check if asset exists by attempting to load it
      const response = await fetch(fullUrl, { method: 'HEAD' });
      if (!response.ok) {
        errors.push(`Asset not found: ${assetPath} (${response.status} ${response.statusText})`);
      }
    } catch (error) {
      errors.push(`Failed to load asset: ${assetPath} - ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Preloads all image assets with progress tracking
 */
async function preloadAssets(
  scenarioId: string,
  assetPaths: string[],
  cacheKey: string,
  onProgress?: ProgressCallback
): Promise<{ success: boolean; errors: string[] }> {
  const errors: string[] = [];
  let loaded = 0;

  // Load all images in parallel with a concurrency limit
  const CONCURRENCY = 5;
  const chunks: string[][] = [];
  
  for (let i = 0; i < assetPaths.length; i += CONCURRENCY) {
    chunks.push(assetPaths.slice(i, i + CONCURRENCY));
  }

  for (const chunk of chunks) {
    await Promise.all(
      chunk.map(async (assetPath) => {
        const fullUrl =
          resolveScenarioAssetUrl(assetPath, scenarioId, cacheKey) ?? assetPath;

        // Check if already cached
        if (imageCache.has(fullUrl)) {
          loaded++;
          if (onProgress) {
            onProgress({
              stage: 'preloading',
              message: 'Preloading assets...',
              percent: 30 + (loaded / assetPaths.length) * 65, // 30-95%
              assetsLoaded: loaded,
              assetsTotal: assetPaths.length,
            });
          }
          return;
        }

        try {
          if (isVideoAsset(assetPath)) {
            await preloadVideo(fullUrl);
            loaded++;
            if (onProgress) {
              onProgress({
                stage: 'preloading',
                message: 'Preloading assets...',
                percent: 30 + (loaded / assetPaths.length) * 65, // 30-95%
                assetsLoaded: loaded,
                assetsTotal: assetPaths.length,
              });
            }
          } else {
            const img = new Image();
            
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                imageCache.set(fullUrl, img);
                loaded++;
                if (onProgress) {
                  onProgress({
                    stage: 'preloading',
                    message: 'Preloading assets...',
                    percent: 30 + (loaded / assetPaths.length) * 65, // 30-95%
                    assetsLoaded: loaded,
                    assetsTotal: assetPaths.length,
                  });
                }
                resolve();
              };
              
              img.onerror = () => {
                errors.push(`Failed to load image: ${assetPath}`);
                loaded++;
                reject(new Error(`Failed to load: ${assetPath}`));
              };
              
              img.src = fullUrl;
            });
          }
        } catch (error) {
          // Error already captured in img.onerror
          console.error(`Error loading asset: ${assetPath}`, error);
        }
      })
    );
  }

  return {
    success: errors.length === 0,
    errors,
  };
}

/**
 * Fetches scenario JSON from backend
 */
async function fetchScenario(scenarioId: string): Promise<Scenario> {
  const response = await fetch(`/api/scenarios/${scenarioId}`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to load scenario: ${response.status} ${response.statusText}`);
  }
  
  const scenario = await response.json();
  return scenario;
}

/**
 * Main ScenarioLoader class
 */
export class ScenarioLoader {
  /**
   * Loads a scenario with full validation and asset preloading
   * 
   * @param scenarioId - ID of the scenario to load
   * @param onProgress - Optional callback for progress updates
   * @param skipCache - If true, bypass cache and reload
   * @returns LoadResult with scenario or error details
   */
  static async load(
    scenarioId: string,
    onProgress?: ProgressCallback,
    skipCache = false
  ): Promise<LoadResult> {
    try {
      // Stage 1: Check cache
      if (!skipCache && scenarioCache.has(scenarioId)) {
        console.log(`Loading scenario from cache: ${scenarioId}`);
        if (onProgress) {
          onProgress({
            stage: 'complete',
            message: 'Loaded from cache',
            percent: 100,
          });
        }
        return {
          success: true,
          scenario: scenarioCache.get(scenarioId)!,
        };
      }

      // Stage 2: Fetch scenario JSON
      if (onProgress) {
        onProgress({
          stage: 'fetching',
          message: 'Fetching scenario...',
          percent: 0,
        });
      }

      const scenario = await fetchScenario(scenarioId);

      if (onProgress) {
        onProgress({
          stage: 'fetching',
          message: 'Scenario fetched',
          percent: 10,
        });
      }

      // Stage 3: Validate scenario structure
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: 'Validating scenario structure...',
          percent: 10,
        });
      }

      const validationResult = validateScenarioStructure(scenario);

      if (!validationResult.isValid) {
        console.error('Scenario validation failed:', validationResult.errors);
        return {
          success: false,
          error: 'Scenario validation failed',
          validationErrors: validationResult.errors.map(e => e.message),
        };
      }

  // Stage 4: Collect and validate assets
      const assetPaths = collectAssetPaths(scenario);
      console.log(`Found ${assetPaths.length} assets to preload`);

      // Resolve asset paths in the scenario object itself
      const resolvedScenario = ScenarioLoader.resolveAssetPaths(scenario, scenarioId);

      if (assetPaths.length > 0) {
        const assetValidation = await validateAssets(scenarioId, assetPaths, onProgress);

        if (!assetValidation.valid) {
          console.error('Asset validation failed:', assetValidation.errors);
          return {
            success: false,
            error: 'Asset validation failed',
            validationErrors: assetValidation.errors,
          };
        }

        // Stage 5: Preload assets
        if (onProgress) {
          onProgress({
            stage: 'preloading',
            message: 'Preloading assets...',
            percent: 30,
            assetsLoaded: 0,
            assetsTotal: assetPaths.length,
          });
        }

        const cacheKey = getScenarioAssetCacheKey(scenario);
        const preloadResult = await preloadAssets(scenarioId, assetPaths, cacheKey, onProgress);

        if (!preloadResult.success) {
          console.error('Asset preloading failed:', preloadResult.errors);
          return {
            success: false,
            error: 'Asset preloading failed',
            validationErrors: preloadResult.errors,
          };
        }
      }

      // Stage 6: Complete
      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: 'Scenario loaded successfully',
          percent: 100,
        });
      }

      // Cache the scenario
      scenarioCache.set(scenarioId, resolvedScenario);

      return {
        success: true,
        scenario: resolvedScenario,
      };
    } catch (error) {
      console.error('Error loading scenario:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Clears the scenario cache
   */
  static clearCache(): void {
    scenarioCache.clear();
  }

  /**
   * Resolves all relative asset paths in a scenario to absolute URLs
   */
  static resolveAssetPaths(
    scenario: Scenario,
    scenarioId: string,
    options?: { layer?: ScenarioAssetLayer },
  ): Scenario {
    const cacheKey = getScenarioAssetCacheKey(scenario);
    const layer = options?.layer ?? 'master';

    const resolve = (url: string | undefined): string | undefined =>
      resolveScenarioAssetUrl(url, scenarioId, cacheKey, { layer });

    const resolveScene = (scene: Scene): Scene => {
      const resolved: Scene = {
        ...scene,
        background: resolve(scene.background),
        elements: scene.elements?.map(element => {
          // Resolve moods if they exist
          const resolvedMoods = element.moods ? { ...element.moods } : undefined;
          if (resolvedMoods) {
            for (const mood in resolvedMoods) {
              resolvedMoods[mood] = resolve(resolvedMoods[mood])!;
            }
          }

          return {
            ...element,
            image: resolve(element.image),
            moods: resolvedMoods,
          };
        }),
      };
      if (resolved.type === 'choice') {
        resolved.options = resolved.options?.map((option) => ({
          ...option,
          scenes: option.scenes?.map(resolveScene),
        }));
      }
      if (resolved.type === 'textOverlay') {
        resolved.characterIcon = resolve(resolved.characterIcon);
      }
      if (resolved.type === 'phoneCall' || resolved.type === 'phoneChat') {
        resolved.contactAvatar = resolve(resolved.contactAvatar);
      }
      return resolved;
    };

    const resolveCharacter = (char: CharacterPortrait): CharacterPortrait => ({
      ...char,
      defaultImage: resolve(char.defaultImage) ?? char.defaultImage,
      icon: resolve(char.icon),
      emotions: char.emotions
        ? Object.fromEntries(
            Object.entries(char.emotions).map(([key, path]) => [key, resolve(path)!])
          )
        : undefined,
    });

    return {
      ...scenario,
      metadata: {
        ...scenario.metadata,
        thumbnail: resolve(scenario.metadata.thumbnail) || '',
        skills: scenario.metadata.skills?.map(skill => ({
          ...skill,
          icon: resolve(skill.icon),
        })),
        characters: scenario.metadata.characters?.map(resolveCharacter),
      },
      items: (scenario.items || []).map(item => ({
        ...item,
        icon: resolve(item.icon) || '',
      })),
      scenes: scenario.scenes.map(resolveScene),
    };
  }

  /**
   * Clears the image cache
   */
  static clearImageCache(): void {
    imageCache.clear();
  }

  /**
   * Clears all caches
   */
  static clearAllCaches(): void {
    scenarioCache.clear();
    imageCache.clear();
  }

  /**
   * Gets a cached image element
   */
  static getCachedImage(url: string): HTMLImageElement | undefined {
    return imageCache.get(url);
  }

  /**
   * Preloads a scenario uploaded for preview mode
   */
  static async loadPreviewScenario(
    scenario: Scenario,
    _scenarioId: string = 'preview',
    onProgress?: ProgressCallback
  ): Promise<LoadResult> {
    try {
      // Stage 1: Validate scenario structure
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: 'Validating scenario structure...',
          percent: 10,
        });
      }

      const validationResult = validateScenarioStructure(scenario);

      if (!validationResult.isValid) {
        console.error('Scenario validation failed:', validationResult.errors);
        return {
          success: false,
          error: 'Scenario validation failed',
          validationErrors: validationResult.errors.map(e => e.message),
        };
      }

      // Stage 2: Complete (assets handled separately in preview mode)
      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: 'Scenario validated successfully',
          percent: 100,
        });
      }

      return {
        success: true,
        scenario,
      };
    } catch (error) {
      console.error('Error loading preview scenario:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}

